<template>
  <VDialogV3
    id="shipment-card"
    position="bottom"

    :entity="localShipment"

    show-edit-button
    :submit-disabled="stateEntity !== null"
    :submit-method="submit"
    :errors="dialogErrors"
    :context-menu="ShipmentsService.contextMenu"

    @edit-button:clicked="showShipmentRequisitesDialog"
  >
    <template #badges>
      <VBadge
        v-if="localShipment?.externalNumber"
        class="external-number"
      >
        Техно №{{ localShipment?.externalNumber }}
      </VBadge>

      <VBadge
        v-if="localShipment?.shipmentToBuyer"
        class="external-number"
      >
        СФТ №{{ localShipment?.shipmentToBuyer.id }}
      </VBadge>

      <VBadge v-if="localShipment.isVerified === true">Сверен</VBadge>
      <VBadge v-if="localShipment.isOfferBased === false" class="primary">Прямой</VBadge>
    </template>

    <header class="document-header">
      <dl>
        <dt>Продавец</dt>
        <dd>{{ localShipment.sellerString }}</dd>
        <dt>Пункт отгрузки</dt>
        <dd>{{ localShipment.sellerStoreString }}</dd>
      </dl>
      <dl>
        <dt>Покупатель</dt>
        <dd>{{ localShipment.buyerString }}</dd>
        <dt>Пункт доставки</dt>
        <dd>{{ localShipment.buyerStoreString }}</dd>
      </dl>
    </header>

    <header class="document-header">
      <dl>
        <dt>Дата отгрузки</dt>
        <dd>{{ localShipment.shipmentDate?.format() || 'Не задано' }}</dd>
        <dt>ТК, доставка</dt>
        <dd>
          {{ localShipment?.delivererName || '--' }},
          {{ localShipment?.trackingNumber || '--' }}
          <template v-if="localShipment.arrivalDatePlanned !== null">
            {{ localShipment.arrivalDatePlanned?.format('D|MM|yyyy') }}
          </template>
        </dd>
      </dl>

      <dl>
        <dt>Ответственный</dt>
        <dd>{{ localShipment.responsibleBuyer?.toString() ?? 'Не выбран' }}</dd>
        <dt>Категория</dt>
        <dd>{{ localShipment.orderCategory?.name ?? 'Не выбран' }}</dd>
      </dl>

      <aside class="totals">
        <section>
          <header>В валюте сделки</header>
          <dl>
            <dt class="total">Итого</dt>
            <dd class="total">
              {{ localShipment.sumFull.format(localShipment.orderCurrency) || '---' }}
            </dd>
            <dt>
              <template v-if="localShipment.isVatIncluded === true">НДС в т.ч</template>
              <template v-else-if="localShipment.isVatIncluded === false">НДС сверху</template>
              <template v-else>НДС не облагается</template>
            </dt>
            <dd>
              {{ localShipment.vatSum?.format(localShipment.orderCurrency) || '---' }}
            </dd>
          </dl>
        </section>

        <section>
          <header>В валюте учёта, курс {{ localShipment.accountingCurrencyRate }}</header>
          <dl>
            <dt class="total">Итого</dt>
            <dd class="total">
              {{
                localShipment.accountingSumFull?.format(localShipment.accountingCurrency) || localShipment.accountingSumFullCalculated?.format(localShipment.accountingCurrency) || '---'
              }}
            </dd>
            <dt>
              <template v-if="localShipment.isVatIncluded === true">НДС в т.ч</template>
              <template v-else-if="localShipment.isVatIncluded === false">НДС сверху</template>
              <template v-else>НДС не облагается</template>
            </dt>
            <dd>
              {{
                localShipment.accountingVatSum?.format(localShipment.accountingCurrency) || localShipment.accountingVatSumCalculated?.format(localShipment.accountingCurrency) || '---'
              }}
            </dd>
          </dl>
        </section>
      </aside>
    </header>

    <VDataGridV2
      :data="localShipment?.positions"
      layout="fill"
    >
      <VDataGridColumnV2
        id="position-action"
        content-align="right"
        width="80px"
      >
        <template #default="{ row }">
          <CommentsButton
            v-if="row.orderPosition !== null && $core.currentPartner.isBuyer === true"
            :entity="row.orderPosition"
            entity-type-name="orderPosition"
          />
        </template>
      </VDataGridColumnV2>

      <VDataGridColumnV2
        id="id"
        content-align="center"
        width="94px"
        content-class="ellipsis"
      >
        <template #label>
          <div>Арт</div>
          <div class="description">№</div>
        </template>
        <template #default="{ row }">
          <VLink
            v-if="row.orderPosition !== null"
            @click="OrdersService.showCodeInfoPopup(row.orderPosition, $event.currentTarget)"
          >
            <template v-if="row.product?.isService === true">
              <template v-if="row.product.code !== null">
                {{ row.product.code }}
              </template>
              <VBadges v-else>
                <VBadge>Услуга</VBadge>
              </VBadges>
            </template>
            <template v-else>
              {{ row.orderPosition.buyersCode }}
            </template>
          </VLink>

          <div
            v-else
            :title="row.product.code"
          >
            <template v-if="row.product.code !== null">
              {{ row.product.code }}
            </template>
            <VBadges v-else>
              <VBadge>Услуга</VBadge>
            </VBadges>
          </div>

          <div class="description">
            {{
              row.orderPosition !== null ? $core.utils.string.formatWithDelimiter(row.orderPosition.id) : ''
            }}
          </div>
        </template>
      </VDataGridColumnV2>

      <VDataGridColumnV2
        id="name"
        content-align="left"
        width="400px"
        content-class="ellipsis"
      >
        <template #label>
          <div>Товар</div>
          <div class="description">
            <template v-if="localShipment.isRawMaterialsAndPackage === true">Производитель
            </template>
            <template v-if="localShipment.isPrinting === true">Материал</template>
          </div>
        </template>
        <template #default="{ row }">
          <span
            v-if="row.product?.isService === true"
            @mouseenter="$core.tooltip.show($event.currentTarget, row.product.name)"
            @mouseleave="$core.tooltip.hide()"
          >
            {{ row.product.name }}
          </span>
          <span
            v-else
            @mouseenter="$core.tooltip.show($event.currentTarget, row.orderPosition.name)"
            @mouseleave="$core.tooltip.hide()"
          >
            {{ row.orderPosition.name }}
          </span>
          <div class="description">
            <template v-if="localShipment.isRawMaterialsAndPackage === true">
              {{ row.orderPosition?.manufacturer?.toString() }}
            </template>
            <template v-if="localShipment.isPrinting === true">{{ row.material }}</template>
          </div>
        </template>
      </VDataGridColumnV2>

      <VDataGridColumnV2
        id="qty"
        content-align="right"
        content-class="form"
        width="140px"
        label="Кол-во"
      >
        <template #default="{ row }">
          <VInputNumberRusV2
            v-model="row.qty"
            :type="Qty"
            required
            autocomplete="off"
            :disabled="localShipment.isReceived === true"
            :title="localShipment.isReceived === true ? 'Отгрузка уже принята' : ''"
            :warning-method="(value) => row.validatePositionQty(value)"
            :unit="row.orderPosition.unit"
          >
            <template #helper-text>
              <template v-if="row.product?.isService !== true">
                <template v-if="row.isShippedEntirely === true">Всё отгружено</template>

                <template v-else-if="row.qty.greaterThan(row.qtyReminder) === false">
                  {{
                    localShipment.isShipmentByAssembly === true ? 'В сборке осталось' : 'В работе осталось'
                  }}
                  {{ row.qtyReminder.minus(row.qty).formatSmart(row.orderPosition.unit) }}
                </template>
              </template>
            </template>
          </VInputNumberRusV2>
        </template>
      </VDataGridColumnV2>

      <VDataGridColumnV2
        id="price-full"
        content-align="right"
        content-class="form"
        width="140px"
      >
        <template #label>
          <div>Цена</div>
          <div class="description">В валюте</div>
        </template>
        <template #default="{ row }">
          <VInputNumberRusV2
            :class="{ 'touched-input': row.accountingPrice?.isPositive() === true }"
            :type="Price"
            required
            :validate-method="$event => ShipmentPosition.validateAccountingValue($event, row.accountingPriceCalculated)"
            :unit="$core.utils.money.getSymbolByCurrency(row.accountingCurrencyCode)"
            :model-value="row.accountingPrice ?? row.accountingPriceCalculated"
            @input="modelValue => (row.accountingPrice = modelValue)"
          />

          <span class="description">
            {{ row.price.format(row.orderCurrency) || '---' }}
          </span>
        </template>
      </VDataGridColumnV2>

      <VDataGridColumnV2
        id="VAT-full"
        content-align="right"
        content-class="form"
        width="170px"
      >
        <template #label>
          <template v-if="typeof localShipment?.isVatIncluded === 'boolean'">
            Сумма НДС{{ localShipment.isVatIncluded === true ? ', в т.ч' : ', сверху' }}
          </template>
          <template v-else>НДС</template>
          <div class="description">В валюте</div>
        </template>
        <template #default="{ row }">
          <VInputNumberRusV2
            :class="{ 'touched-input': row.accountingVatSum?.isPositive() === true }"
            :type="Sum"
            required
            :unit="$core.utils.money.getSymbolByCurrency(row.accountingCurrencyCode)"
            :disabled="typeof localShipment?.isVatIncluded !== 'boolean'"
            :title="typeof localShipment?.isVatIncluded === 'boolean' ? null : 'НДС не облагается'"
            :validate-method="$event => ShipmentPosition.validateAccountingValue($event, row.accountingVatSumCalculated)"
            :model-value="row.accountingVatSum ?? row.accountingVatSumCalculated"
            @input="modelValue => (row.accountingVatSum = modelValue)"
          />

          <span class="description">
            {{ row.vatSum?.format(row.orderCurrency) || '---' }}
          </span>
        </template>
      </VDataGridColumnV2>

      <VDataGridColumnV2
        id="sum-full"
        content-align="right"
        content-class="form"
        width="170px"
      >
        <template #label>
          Сумма полная
          <div class="description">В валюте</div>
        </template>
        <template #default="{ row }">
          <VInputNumberRusV2
            ref="sumAccounting"
            :class="{ 'touched-input': row.accountingSumFull?.isPositive() === true }"
            :type="Sum"
            required
            :unit="$core.utils.money.getSymbolByCurrency(row.accountingCurrencyCode)"
            :validate-method="$event => ShipmentPosition.validateAccountingValue($event, row.accountingSumFullCalculated)"
            :model-value="row.accountingSumFull ?? row.accountingSumFullCalculated"
            @input="modelValue => (row.accountingSumFull = modelValue)"
          />

          <span class="description">
            {{ row.sumFull.format(row.orderCurrency) || '---' }}
          </span>
        </template>
      </VDataGridColumnV2>

      <VDataGridColumnV2
        id="actions"
        width="30px"
        content-class="form"
      >
        <template #default="{ row }">
          <VButton
            :disabled="deleteDisabled"
            :title="deleteDisabledReason"
            @click="localShipment.deletePosition(row)"
            material="delete"
          />
        </template>
      </VDataGridColumnV2>

      <VDataGridActionBarV2
        class="centered"
        :disabled="localShipment.isReceived === true ? 'Отгрузка принята — позиции редактировать нельзя' : null">
        <VLink @click="showPositionAddDialog" material="add">
          Добавить позицию
        </VLink>

        <!--        <VLink-->
        <!--          :disabled="localShipment?.positions.length === 0 ? 'Сначала добавьте позиции' : null"-->
        <!--          @click="showAddServicePositionDialog"-->
        <!--        >-->
        <!--          <template #icon>-->
        <!--            <VIcon material="add"/>-->
        <!--          </template>-->
        <!--          Добавить услугу-->
        <!--        </VLink>-->
      </VDataGridActionBarV2>
    </VDataGridV2>
  </VDialogV3>
</template>

<script lang="ts">

import Shipment from '@/domains/Shipments/Shipment';
import type Assembly from '@/domains/Assemblies/Assembly';
import type OrderPosition from '@/domains/Orders/OrderPosition';
import ShipmentsService from '@/domains/Shipments/ShipmentsService';
import AssembliesService from "@/domains/Assemblies/AssembliesService";
import OrdersService from '@/domains/Orders/OrdersService';
import ShipmentPosition from "@/domains/Shipments/ShipmentPosition";
import Qty from '@/core/types/Qty';
import Price from '@/core/types/Price';
import Sum from '@/core/types/Sum';

export default defineComponent({
  name: 'ShipmentCard',

  props: {
    /** ID отгрузки — передаётся диалоговой системой (роутер/deep link). */
    id: {
      type: Number,
      default: null,
    },
    /** Готовый объект сборки — для создания отгрузки на её основе. */
    assembly: {
      type: Object as PropType<Assembly>,
      default: null,
    },
    /** Позиции заказа, из которых создаётся новая отгрузка. */
    orderPositions: {
      type: Array as PropType<OrderPosition[]>,
      default: null,
    },
    isInWork: {
      type: Boolean,
      default: false,
    },
  },


  setup() {
    return {
      ShipmentsService,
      OrdersService,
      ShipmentPosition,
      Qty,
      Price,
      Sum,
    }
  },


  data() {
    return {
      localShipment: undefined as unknown as Shipment,

      /**
       * Будет использовано в проверке, не изменял ли пользователь склад
       * поставщика, чтобы не запрашивать подтверждение при каждом редактировании
       */
      originalSellerStoreId: undefined as number | undefined,
    };
  },

  computed: {
    hasPositions() {
      return Array.isArray(this.localShipment?.positions)
        ? this.localShipment?.positions.length > 0
        : false;
    },

    isValidPositions() {
      if (!Array.isArray(this.localShipment?.positions)) {
        return false;
      }
      if (!this.localShipment?.positions.length) {
        return false;
      }

      for (let i = 0; i < this.localShipment.positions.length; i++) {
        if (this.localShipment.positions[i].qty.isEmpty()) {
          return false;
        }
      }
      return true;
    },

    isValidVatType() {
      if (!this.localShipment?.positions.length) {
        return false;
      }
      for (let i = 0; i < this.localShipment.positions.length; i++) {
        if (this.localShipment.positions[i].isVatIncluded !== this.localShipment.isVatIncluded) {
          return false;
        }
      }
      return true;
    },

    isValidCurrencyCode() {
      if (this.hasPositions && this.localShipment?.positions.length > 1) {
        for (let i = 1; i < this.localShipment?.positions.length; i++) {
          if (this.localShipment?.positions[i].orderCurrencyCode !== this.localShipment?.orderCurrencyCode) {
            return false;
          }
        }
      }
      return true;
    },

    stateEntity() {
      if (!this.hasPositions) {
        return 'Добавьте позиции';
      }

      if (!this.isValidCurrencyCode) {
        return 'Нельзя отгрузить позиции с разными валютами сделки';
      }

      if (!this.isValidPositions) {
        return 'Заполните желаемое количество товара';
      }

      if (!this.isValidVatType) {
        return 'Нельзя отгрузить позиции с разными типами НДС';
      }

      return null;
    },

    dialogErrors() {
      if (this.stateEntity) {
        return [{message: this.stateEntity}];
      } else {
        return [];
      }
    },
    deleteDisabled() {
      return this.localShipment.positions.length === 1 || this.localShipment.isReceived === true;
    },

    deleteDisabledReason() {
      if (this.localShipment.isReceived === true) {
        return 'Отгрузка уже принята';
      }
      if (this.localShipment.positions.length === 1) {
        return 'Нельзя удалить единственную позицию';
      }
      return '';
    },
  },

  async mounted() {
    await this.init();

    if (this.localShipment?.isReceived === false && this.localShipment?.isExisting === false) {
      if (this.localShipment.isShipmentByAssembly === true && this.localShipment.positions.length === 0) {
        /** Сборку целиком одним документом не отгрузить: сначала набираем позиции,
         *  реквизиты после — курс считается от валюты первой позиции */
        await this.showPositionAddDialog();

        if (this.localShipment.positions.length === 0) {
          this.$core.dialogs.ShipmentCard.hide();
        } else {
          this.showShipmentRequisitesDialog();
        }
      } else {
        this.showShipmentRequisitesDialog();
      }
    }
  },

  methods: {
    async init() {
      if (this.id) {
        this.localShipment = await ShipmentsService.get(this.id);
      } else if (this.assembly) {
        // Отгрузка по сборочному листу
        const assembly = await AssembliesService.get(this.assembly.id);
        this.localShipment = Shipment.fromAssembly(assembly);
      } else if (this.$core.utils.isNotEmptyArray(this.orderPositions)) {
        // Отгрузка под выбранные позиции заказа
        this.localShipment = Shipment.fromOrderPositions(this.orderPositions);
      } else {
        // Создаём новую с нуля
        this.localShipment = Shipment.fromNew();
      }

      this.originalSellerStoreId = this.localShipment?.sellerStore?.id;
    },

    showShipmentRequisitesDialog() {
      this.$core.dialogs.ShipmentRequisites.show(
        {
          isInWork: this.isInWork,
          shipment: this.localShipment
        },
        {
          updated: (shipment: Shipment) => {
            this.localShipment = shipment;
          },
          close: () => {
            /** Реквизиты закрыли, не заполнив: несохранённой отгрузке без них
             *  делать нечего — закрываем и саму карточку. */
            if (this.localShipment?.isExisting !== true) {
              this.$core.dialogs.ShipmentCard.hide();
            }
          },
        },
      );
    },

    async submit(): Promise<boolean> {
      let confirmed = true;

      if (this.originalSellerStoreId !== this.localShipment.sellerStore?.id) {
        confirmed = await this.$core.confirm(`Подтверждаете отгрузку со склада: ${this.localShipment.sellerStoreString}?`);
      }

      if (confirmed === true) {
        if (this.localShipment.isExisting === true) {
          return await ShipmentsService.update(this.localShipment);
        } else {
          return await ShipmentsService.add(this.localShipment);
        }
      } else {
        this.showShipmentRequisitesDialog();
        return false;
      }
    },

    async showPositionAddDialog(): Promise<void> {
      // если отгрузка основана на сборке, то открываем реестр сборок
      if (this.localShipment?.isShipmentByAssembly) {
        let params = null;

        /** Отгрузка создаётся по конкретной сборке — её позиции и показываем */
        if (this.assembly !== null) {
          params = { id: this.assembly.id };
        }

        await this.$core.dialogs.AssembliesRegistryExpandedDialog.showAsPromise({
          shipment: this.localShipment,
          params,
        });
      } else {
        // если отгрузка свободная, то реестр позиций
        this.$core.dialogs.OrderPositionsRegistryDialog.show({
          targetDocument: this.localShipment,
          state: 'in-work',
          params: {
            // в отгрузку добираем позиции того же происхождения, что и у самой отгрузки
            isOfferBased: this.localShipment.isOfferBased,
            seller: this.localShipment.seller,
          }
        })
      }
    },

    showAddServicePositionDialog() {
      this.$core.dialogs.AddServicePositionDialog.show({shipment: this.localShipment});
    },
  },
});
</script>